// Client mirror of backend design-canvas grading (live task ticking).
const norm = (hex) => String(hex || "").trim().toLowerCase();

export function luminance(hex) {
  const h = norm(hex).replace("#", "");
  if (h.length !== 6) return 0;
  const c = [0, 2, 4].map((i) => {
    const v = parseInt(h.slice(i, i + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
}

export function contrastRatio(a, b) {
  const la = luminance(a), lb = luminance(b);
  const hi = Math.max(la, lb), lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

const objs = (doc) => doc.objects || [];
const byId = (doc, id) => objs(doc).find((o) => o.id === id);
const zIndex = (doc, id) => objs(doc).findIndex((o) => o.id === id);
const box = (o) => ({ x: o.x, y: o.y, w: o.w, h: o.h, cx: o.x + o.w / 2, cy: o.y + o.h / 2, r: o.x + o.w, b: o.y + o.h });
const hasStroke = (o) => (o.strokeWidth || 0) > 0;
const isText = (o) => o.type === "text" && String(o.text || "").trim();

// Background colour behind a text object: topmost shape under it, else the canvas.
function backdropOf(doc, t) {
  const list = objs(doc);
  const ti = list.indexOf(t);
  const c = box(t);
  for (let i = ti - 1; i >= 0; i--) {
    const o = list[i];
    if (o.type === "text" || o.type === "line" || o.type === "elbow" || o.fill === "none") continue;
    const b = box(o);
    if (c.cx >= b.x && c.cx <= b.r && c.cy >= b.y && c.cy <= b.b) return o.fill;
  }
  return (doc.canvas || {}).bg || "#FFFFFF";
}

const EDGE = {
  left: (b) => b.x, right: (b) => b.r, centerX: (b) => b.cx,
  top: (b) => b.y, bottom: (b) => b.b, centerY: (b) => b.cy,
};

const MEDIA_TYPES = ["image", "gallery"];

function checkSite(check, doc) {
  const k = check.kind;
  const sections = doc.sections || [];
  const site = doc.site || {};
  const theme = site.theme || {};
  const types = sections.map((s) => s.type);
  const ofType = (t) => sections.filter((s) => s.type === t);
  const media = () => sections.filter((s) => MEDIA_TYPES.includes(s.type));
  switch (k) {
    case "section_count": {
      const n = sections.filter((s) => !check.types || check.types.includes(s.type)).length;
      return n >= (check.min ?? 1) && (check.max == null || n <= check.max);
    }
    case "section_absent": return !types.some((t) => check.types.includes(t));
    case "section_order": return JSON.stringify(types) === JSON.stringify(check.types);
    case "section_first": return sections.length > 0 && sections[0].type === check.type;
    case "section_last": return sections.length > 0 && sections[sections.length - 1].type === check.type;
    case "section_field": {
      const rows = ofType(check.type);
      if (!rows.length) return false;
      const ok = (s) => {
        const v = s[check.field];
        if (check.min_words) return String(v || "").trim().split(/\s+/).filter(Boolean).length >= check.min_words;
        if (check.equals != null) return norm(v) === norm(check.equals);
        return !!String(v || "").trim();
      };
      return check.all ? rows.every(ok) : rows.some(ok);
    }
    case "cards_filled": {
      const rows = ofType("cards");
      return rows.some((s) => (s.cards || []).filter((c) => String(c.title || "").trim() && String(c.body || "").trim()).length >= (check.min ?? 3));
    }
    case "nav_min": return (site.navLinks || []).filter((x) => String(x).trim()).length >= check.min;
    case "theme_attr": return norm(theme[check.attr]) === norm(check.equals);
    case "theme_contrast": return contrastRatio(theme.text, theme.bg) >= (check.min ?? 4.5);
    case "template_used": return site.template === check.equals;
    case "template_not_blank": return !!site.template && site.template !== "blank";
    case "alt_all": { const r = media(); return r.length > 0 && r.every((s) => String(s.alt || "").trim()); }
    case "web_optimized": { const r = media(); return r.length > 0 && r.every((s) => s.quality === "web"); }
    case "license_all_set": { const r = media(); return r.length > 0 && r.every((s) => String(s.license || "").trim()); }
    case "no_arr_license": { const r = media(); return r.length > 0 && r.every((s) => s.license !== "arr"); }
    case "credit_all": { const r = media(); return r.length > 0 && r.every((s) => String(s.credit || "").trim()); }
    case "previewed_mobile": return !!doc.previewedMobile;
    case "ai_used": return !!doc.aiUsed;
    case "ai_applied": return !!doc.aiApplied;
    case "site_published": return !!doc.published;
    default: return false;
  }
}

const SITE_KINDS = new Set(["section_count", "section_absent", "section_order", "section_first", "section_last",
  "section_field", "cards_filled", "nav_min", "theme_attr", "theme_contrast", "template_used",
  "template_not_blank", "alt_all", "web_optimized", "license_all_set", "no_arr_license",
  "credit_all", "previewed_mobile", "ai_used", "ai_applied", "site_published"]);

export function checkDraw(check, doc) {
  const k = check.kind;
  if (SITE_KINDS.has(k)) return checkSite(check, doc);
  const list = objs(doc);
  const pick = () => {
    let s = list;
    if (check.types) s = s.filter((o) => check.types.includes(o.type));
    if (check.ids) s = s.filter((o) => check.ids.includes(o.id));
    return s;
  };
  switch (k) {
    case "design_named": {
      const n = String(doc.name || "").trim();
      if (!n || n.toLowerCase() === "untitled design" || n.toLowerCase() === "untitled site") return false;
      return check.contains ? n.toLowerCase().includes(check.contains.toLowerCase()) : true;
    }
    case "obj_count": return pick().length >= (check.min ?? 1) && (check.max == null || pick().length <= check.max);
    case "obj_count_exact": return pick().length === check.equals;
    case "canvas_bg": return norm((doc.canvas || {}).bg) === norm(check.hex);
    case "canvas_grid": return !!(doc.canvas || {}).grid;
    case "canvas_size": return (doc.canvas || {}).w === check.w && (doc.canvas || {}).h === check.h;
    case "fill_count": return list.filter((o) => norm(o.fill) === norm(check.hex)).length >= (check.min ?? 1);
    case "stroke_count": return list.filter((o) => hasStroke(o) && norm(o.stroke) === norm(check.hex)).length >= (check.min ?? 1);
    case "stroke_width_count": return pick().filter((o) => (o.strokeWidth || 0) >= check.min_width).length >= (check.min ?? 1);
    case "dash_count": return list.filter((o) => hasStroke(o) && (o.dash || "solid") === check.equals).length >= (check.min ?? 1);
    case "opacity_count": return list.filter((o) => (o.opacity ?? 1) <= (check.max ?? 0.9)).length >= (check.min ?? 1);
    case "rotated_count": return list.filter((o) => Math.round(o.rot || 0) % 360 !== 0).length >= (check.min ?? 1);
    case "arrowhead_count": return list.filter((o) => (o.type === "line" || o.type === "elbow") && (o.arrowEnd || o.arrowStart)).length >= (check.min ?? 1);
    case "size_count": {
      let s = pick();
      if (check.min_w) s = s.filter((o) => (o.w || 0) >= check.min_w);
      if (check.min_h) s = s.filter((o) => (o.h || 0) >= check.min_h);
      return s.length >= (check.min ?? 1);
    }
    case "text_count": return list.filter(isText).length >= (check.min ?? 1);
    case "design_text_contains": return list.some((o) => String(o.text || "").toLowerCase().includes(String(check.value).toLowerCase()));
    case "font_size_count": return list.filter((o) => isText(o) && (o.fontSize || 0) >= check.min_size).length >= (check.min ?? 1);
    case "font_family_count": return list.filter((o) => isText(o) && o.fontFamily === check.equals).length >= (check.min ?? 1);
    case "bold_count": return list.filter((o) => isText(o) && o.bold).length >= (check.min ?? 1);
    case "has_image": {
      let imgs = list.filter((o) => o.type === "image" && o.imageId);
      if (check.image_id) imgs = imgs.filter((o) => o.imageId === check.image_id);
      if (check.alt) imgs = imgs.filter((o) => String(o.alt || "").trim());
      return imgs.length >= (check.min ?? 1);
    }
    case "has_video": {
      let vs = list.filter((o) => o.type === "video" && o.videoId);
      if (check.video_id) vs = vs.filter((o) => o.videoId === check.video_id);
      return vs.length >= (check.min ?? 1);
    }
    case "anim_count": {
      let a = list.filter((o) => (o.anim || "none") !== "none");
      if (check.equals) a = a.filter((o) => o.anim === check.equals);
      return a.length >= (check.min ?? 1);
    }
    case "notes_min_words": return String(doc.notes || "").trim().split(/\s+/).filter(Boolean).length >= check.min;
    case "exported": return !!doc.exported;
    case "png_exported": return !!doc.downloadedPng;
    case "obj_attr": { const o = byId(doc, check.id); if (!o) return false; const v = o[check.attr]; return typeof check.equals === "string" && typeof v === "string" ? norm(v) === norm(check.equals) : v === check.equals; }
    case "obj_attr_min": { const o = byId(doc, check.id); return !!o && (o[check.attr] || 0) >= check.min; }
    case "obj_exists": return !!byId(doc, check.id);
    case "obj_z_above": { const a = zIndex(doc, check.a), b = zIndex(doc, check.b); return a >= 0 && b >= 0 && a > b; }
    case "obj_z_top": { const i = zIndex(doc, check.id); return i >= 0 && i === list.length - 1; }
    case "obj_z_bottom": return zIndex(doc, check.id) === 0;
    case "obj_z_order": {
      const idx = (check.ids || []).map((id) => zIndex(doc, id));
      return idx.every((v) => v >= 0) && idx.every((v, i) => i === 0 || idx[i - 1] < v);
    }
    case "grouped": {
      const sel = (check.ids || []).map((id) => byId(doc, id));
      if (sel.some((o) => !o)) return false;
      const g = sel[0].group;
      return !!g && sel.every((o) => o.group === g);
    }
    case "group_min": {
      const counts = {};
      list.forEach((o) => { if (o.group) counts[o.group] = (counts[o.group] || 0) + 1; });
      return Object.values(counts).some((n) => n >= (check.min ?? 2));
    }
    case "ungrouped": {
      const sel = check.ids ? (check.ids || []).map((id) => byId(doc, id)).filter(Boolean) : list;
      return sel.length > 0 && sel.every((o) => !o.group);
    }
    case "aligned": {
      const sel = check.ids ? (check.ids || []).map((id) => byId(doc, id)) : pick();
      if (sel.some((o) => !o) || sel.length < 2) return false;
      const f = EDGE[check.edge];
      const vals = sel.map((o) => f(box(o)));
      return Math.max(...vals) - Math.min(...vals) <= (check.tol ?? 4);
    }
    case "spaced_evenly": {
      const sel = (check.ids || []).map((id) => byId(doc, id));
      if (sel.some((o) => !o) || sel.length < 3) return false;
      const xs = sel.map((o) => box(o).cx).sort((a, b) => a - b);
      const gaps = xs.slice(1).map((v, i) => v - xs[i]);
      return Math.max(...gaps) - Math.min(...gaps) <= (check.tol ?? 12);
    }
    case "near": {
      const sel = (check.ids || []).map((id) => byId(doc, id));
      if (sel.some((o) => !o) || sel.length < 2) return false;
      const gap = (a, b) => Math.max(0, Math.max(a.x - b.r, b.x - a.r)) + Math.max(0, Math.max(a.y - b.b, b.y - a.b));
      for (let i = 1; i < sel.length; i++) {
        const near = sel.slice(0, i).some((p) => gap(box(sel[i]), box(p)) <= (check.max_gap ?? 40));
        if (!near) return false;
      }
      return true;
    }
    case "far_apart": {
      const a = byId(doc, check.a), b = byId(doc, check.b);
      if (!a || !b) return false;
      const A = box(a), B = box(b);
      const gap = Math.max(0, Math.max(A.x - B.r, B.x - A.r)) + Math.max(0, Math.max(A.y - B.b, B.y - A.b));
      return gap >= (check.min_gap ?? 60);
    }
    case "repeated": {
      const attrs = check.attrs || ["type", "fill", "w", "h"];
      const counts = {};
      list.filter((o) => !isText(o)).forEach((o) => {
        const key = attrs.map((a) => (typeof o[a] === "string" ? norm(o[a]) : o[a])).join("|");
        counts[key] = (counts[key] || 0) + 1;
      });
      return Object.values(counts).some((n) => n >= (check.min ?? 3));
    }
    case "contrast_min": {
      const texts = list.filter(isText);
      if (!texts.length) return false;
      const ok = texts.filter((t) => contrastRatio(t.color, backdropOf(doc, t)) >= (check.min ?? 4.5));
      return check.all ? ok.length === texts.length : ok.length >= (check.count ?? 1);
    }
    case "balanced": {
      const vis = list.filter((o) => o.type !== "line" && o.type !== "elbow");
      if (vis.length < 2) return false;
      const c = doc.canvas || { w: 960, h: 540 };
      const area = vis.reduce((s, o) => s + o.w * o.h, 0);
      if (!area) return false;
      const cx = vis.reduce((s, o) => s + box(o).cx * o.w * o.h, 0) / area;
      return Math.abs(cx - c.w / 2) <= (check.tol ?? 60);
    }
    default: return false;
  }
}
